
import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Clipboard, Image, ClipboardPaste } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { optimizeThumbnail } from "@/utils/imageUtils";

interface ManualThumbnailDialogProps {
  contentId: string;
  contentTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ManualThumbnailDialog = ({
  contentId,
  contentTitle,
  open,
  onOpenChange,
}: ManualThumbnailDialogProps) => {
  const [image, setImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  
  const handlePaste = useCallback(async (e: React.ClipboardEvent | null) => {
    console.log("Paste event detected");
    
    const processClipboardItems = async (items: DataTransferItemList) => {
      let imageFound = false;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          imageFound = true;
          const blob = items[i].getAsFile();
          if (blob) {
            const url = URL.createObjectURL(blob);
            setImage(url);
            console.log("Image from clipboard:", url);
          }
          break;
        }
      }

      if (!imageFound) {
        toast.error("No image found in clipboard");
      }
    };
    
    if (e && e.clipboardData) {
      await processClipboardItems(e.clipboardData.items);
    } 
    else {
      try {
        const clipboardItems = await navigator.clipboard.read();
        
        for (const clipboardItem of clipboardItems) {
          const imageTypes = clipboardItem.types.filter(type => type.startsWith('image/'));
          
          if (imageTypes.length > 0) {
            const blob = await clipboardItem.getType(imageTypes[0]);
            const url = URL.createObjectURL(blob);
            setImage(url);
            console.log("Image from Clipboard API:", url);
            return;
          }
        }
        
        toast.error("No image found in clipboard");
      } catch (err) {
        console.error("Clipboard API error:", err);
        toast.error("Could not access clipboard. Try using keyboard shortcut instead.");
      }
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      console.log("Ctrl+V detected in container");
    }
  }, []);

  const handlePasteButtonClick = async () => {
    console.log("Paste button clicked, attempting to access clipboard");
    try {
      await handlePaste(null);
    } catch (error) {
      console.error("Error with paste button:", error);
      toast.error("Failed to paste from clipboard. Try using keyboard shortcut.");
    }
  };

  const handleSave = async () => {
    if (!image) {
      toast.error("Please paste an image first");
      return;
    }

    setIsUploading(true);
    
    try {
      // Fetch the image as a blob
      const response = await fetch(image);
      const originalBlob = await response.blob();
      
      // Apply optimization to the image (convert to WebP, resize, compress)
      console.log("Optimizing image...");
      const optimizedBlob = await optimizeThumbnail(originalBlob);
      
      // Log size comparison for debugging
      const originalSize = (originalBlob.size / 1024).toFixed(2);
      const optimizedSize = (optimizedBlob.size / 1024).toFixed(2);
      const savings = (100 - (optimizedBlob.size / originalBlob.size) * 100).toFixed(1);
      console.log(`Image optimization: ${originalSize}KB → ${optimizedSize}KB (${savings}% smaller)`);
      
      // Always use .webp extension for the optimized file
      const fileName = `${contentId}_manual_${Date.now()}.webp`;
      
      console.log(`Uploading to thumbnails bucket with filename: ${fileName}`);
      
      const { data, error } = await supabase.storage
        .from('thumbnails')
        .upload(`public/${fileName}`, optimizedBlob, {
          cacheControl: '3600',
          upsert: true,
          contentType: 'image/webp'
        });
      
      if (error) {
        console.error("Storage upload error:", error);
        throw error;
      }
      
      const { data: { publicUrl } } = supabase.storage
        .from('thumbnails')
        .getPublicUrl(`public/${fileName}`);
      
      console.log("Generated public URL:", publicUrl);
      
      const { error: updateError } = await supabase
        .from('content_items')
        .update({ 
          thumbnail_url: publicUrl,
          metadata: {
            thumbnail_source: 'manual',
            thumbnail_updated_at: new Date().toISOString(),
            thumbnail_optimized: true,
            original_size_kb: Number(originalSize),
            optimized_size_kb: Number(optimizedSize),
            optimization_savings_percent: Number(savings)
          }
        })
        .eq('id', contentId);
      
      if (updateError) throw updateError;
      
      queryClient.invalidateQueries();
      
      toast.success("Thumbnail updated successfully");
      
      console.log("[ManualThumbnailDialog] Refreshing current page without navigation");
      const currentUrl = window.location.href;
      console.log("[ManualThumbnailDialog] Current URL:", currentUrl);
      
      setTimeout(() => {
        window.location.href = currentUrl;
      }, 300);
      
    } catch (error: any) {
      console.error("Error saving thumbnail:", error);
      toast.error(error.message || "Failed to save thumbnail");
    } finally {
      setIsUploading(false);
    }
  };

  const clearImage = () => {
    setImage(null);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      onOpenChange(newOpen);
      
      if (!newOpen && open) {
        console.log('[ManualThumbnailDialog] Dialog closing, refreshing current page without navigation');
        const currentUrl = window.location.href;
        console.log("[ManualThumbnailDialog] Maintaining URL:", currentUrl);
        setTimeout(() => {
          window.location.href = currentUrl;
        }, 300);
      }
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Manual Thumbnail</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div 
            ref={divRef}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            className="focus:outline-none"
          >
            <AspectRatio ratio={16/9}>
              {image ? (
                <div className="relative h-full w-full">
                  <img 
                    src={image} 
                    alt="Thumbnail preview" 
                    className="h-full w-full object-cover rounded-md"
                  />
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="absolute top-2 right-2"
                    onClick={clearImage}
                  >
                    Clear
                  </Button>
                </div>
              ) : (
                <div className="h-full w-full flex flex-col items-center justify-center gap-4 bg-gray-100 rounded-md border-2 border-dashed border-gray-300">
                  <button 
                    className="h-full w-full flex flex-col items-center justify-center cursor-pointer"
                    onClick={handlePasteButtonClick}
                    type="button"
                    aria-label="Paste image from clipboard"
                  >
                    <Clipboard size={48} className="text-gray-400 mb-2" />
                    <p className="font-medium text-center">Paste image from clipboard</p>
                    <p className="text-sm text-gray-500 mt-1 text-center">Press Ctrl+V or ⌘+V to paste</p>
                    
                    <Button 
                      variant="secondary"
                      size="lg"
                      className="mt-4 flex items-center gap-2"
                    >
                      <ClipboardPaste size={16} />
                      <span>Paste Image</span>
                    </Button>
                  </button>
                </div>
              )}
            </AspectRatio>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!image || isUploading}
            >
              {isUploading ? "Saving..." : "Save Thumbnail"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManualThumbnailDialog;
